import React, { useState, useEffect, useRef } from 'react';
import { OperatorId, Operator, AirtimeDenomination, Wallet, getAvailableBalance } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import {
  X,
  Check,
  ArrowRight,
  ShieldCheck,
  Copy,
  Upload,
  Send,
  Wallet as WalletIcon,
  CheckCircle2,
  PhoneCall,
  AlertCircle,
} from 'lucide-react';
import { OperatorLogo, PaymentLogo } from '../common/BrandLogo';
import { compressImage } from '../../lib/image';

interface BuyBillModalProps {
  operators: Operator[];
  wallet?: Wallet | null;
  initialOperatorId?: OperatorId;
  onClose: () => void;
  onSuccess: () => void;
}

export const BuyBillModal: React.FC<BuyBillModalProps> = ({
  operators,
  wallet,
  initialOperatorId = 'mpt',
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { isGuest } = useAuth();
  const [operatorId, setOperatorId] = useState<OperatorId>(initialOperatorId);
  const [denominations, setDenominations] = useState<AirtimeDenomination[]>([]);
  const [selectedDenomination, setSelectedDenomination] = useState<AirtimeDenomination | null>(null);
  const [recipientPhone, setRecipientPhone] = useState('09');
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'direct_kpay' | 'direct_wave'>('wallet');
  const [txRef, setTxRef] = useState('');
  const [slipUrl, setSlipUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [receivingAccounts, setReceivingAccounts] = useState<any[]>([]);
  const [submittedOrder, setSubmittedOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDenominations, setLoadingDenominations] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Available balance
  const availableBalance = getAvailableBalance(wallet);
  const currentOperator = operators.find((op) => op.id === operatorId);
  const isOperatorClosed = currentOperator && currentOperator.bill_sell_status === 'closed';

  // Fetch active receiving accounts
  useEffect(() => {
    api.getReceivingAccounts().then((accs) => {
      if (accs && accs.length > 0) {
        setReceivingAccounts(accs);
      }
    }).catch(() => {});
  }, []);

  // Fetch active airtime denominations when operator changes
  useEffect(() => {
    setLoadingDenominations(true);
    api.getAirtimeDenominations(operatorId)
      .then((dens) => {
        setDenominations(dens);
        if (dens && dens.length > 0) {
          // Default to 5000 or first available
          const def = dens.find((d) => d.airtime_amount === 5000) || dens[0];
          setSelectedDenomination(def);
        } else {
          setSelectedDenomination(null);
        }
      })
      .catch(() => {
        setDenominations([]);
        setSelectedDenomination(null);
      })
      .finally(() => {
        setLoadingDenominations(false);
      });
  }, [operatorId]);

  // If wallet balance is insufficient for selected denomination, auto switch or prompt
  useEffect(() => {
    if (selectedDenomination) {
      if (availableBalance < selectedDenomination.selling_price && paymentMethod === 'wallet' && !isGuest) {
        setPaymentMethod('direct_kpay');
      }
    }
  }, [selectedDenomination, availableBalance, isGuest]);

  const activePaymentAccount = receivingAccounts.find((a) => {
    if (paymentMethod === 'direct_kpay') {
      return a.type === 'payment' && (a.provider.toLowerCase().includes('kpay') || a.id.includes('kpay'));
    }
    if (paymentMethod === 'direct_wave') {
      return a.type === 'payment' && (a.provider.toLowerCase().includes('wave') || a.id.includes('wave'));
    }
    return false;
  });

  const displayNumber = activePaymentAccount?.account_number || '09987654321';
  const displayName = activePaymentAccount?.account_name || 'PluvP Shop';

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    if (!file.type.startsWith('image/') && !file.name.match(/\.(jpg|jpeg|png|webp|heic|bmp|gif)$/i)) {
      setError('ကျေးဇူးပြု၍ ဓာတ်ပုံ သို့မဟုတ် Screenshot သာ ရွေးချယ်ပေးပါ');
      return;
    }

    try {
      setPreviewUrl(URL.createObjectURL(file));
    } catch {
      // ignore
    }

    setCompressing(true);
    setError('');

    try {
      const dataUrl = await compressImage(file);
      setSlipUrl(dataUrl);
    } catch {
      try {
        const rawData = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        setSlipUrl(rawData);
      } catch {
        setError('ပုံရိပ် တင်သွင်းမှု မအောင်မြင်ပါ။ နောက်တစ်ကြိမ် ကြိုးစားပေးပါ။');
        setPreviewUrl('');
      }
    } finally {
      setCompressing(false);
    }
  };

  const handleSubmit = async () => {
    setError('');

    if (isOperatorClosed) {
      setError(`${currentOperator?.name || operatorId.toUpperCase()} ဖုန်းဘေလ်အရောင်းကို ယာယီပိတ်ထားပါသည်`);
      return;
    }

    if (!selectedDenomination) {
      setError('ဝယ်ယူလိုသည့် ဘေလ်ပမာဏကို ရွေးချယ်ပေးပါ');
      return;
    }

    const cleanPhone = recipientPhone.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 8 || cleanPhone.length > 13) {
      setError('ဘေလ်လက်ခံမည့် ဖုန်းနံပါတ် အပြည့်အစုံ ထည့်သွင်းပေးပါ');
      return;
    }

    if (paymentMethod !== 'wallet' && !slipUrl) {
      setError('ကျေးဇူးပြု၍ ငွေလွှဲပြေစာ Screenshot ထည့်သွင်းပေးပါ');
      fileInputRef.current?.click();
      return;
    }

    if (compressing) {
      setError('ပုံရိပ် ချုံ့နေဆဲဖြစ်ပါသည်။ ခေတ္တစောင့်ဆိုင်းပြီး ထပ်မံနှိပ်ပေးပါ။');
      return;
    }

    setLoading(true);

    try {
      const order = await api.createOrder({
        operatorId,
        orderType: 'phone_bill',
        recipientPhone: cleanPhone,
        airtimeAmount: selectedDenomination.airtime_amount,
        amount: selectedDenomination.selling_price,
        paymentMethod,
        txRef: paymentMethod !== 'wallet' ? (txRef.trim() || undefined) : undefined,
        slipUrl: paymentMethod !== 'wallet' ? slipUrl : undefined,
      });

      setSubmittedOrder(order);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'ဖုန်းဘေလ်ဝယ်ယူမှု မအောင်မြင်ပါ။');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm p-0 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-surface-card w-full max-w-md rounded-t-3xl sm:rounded-3xl border border-plum-border shadow-2xl overflow-hidden max-h-[92vh] flex flex-col animate-in slide-in-from-bottom-5">
        {/* Header */}
        <div className="px-5 py-4 border-b border-plum-border flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <PhoneCall className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-bold text-plum-dark text-sm sm:text-base">ဖုန်းဘေလ်ဝယ်မည်</h2>
              <p className="text-[11px] text-plum-muted">လွှဲခ အခမဲ့ / Transfer fee included</p>
            </div>
          </div>
          <button
            onClick={() => {
              if (submittedOrder) onSuccess();
              onClose();
            }}
            className="p-1.5 rounded-full hover:bg-surface-page text-plum-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {/* Guest prompt if not in Telegram */}
          {isGuest ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
                <Send className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-plum-dark text-sm">Telegram Bot မှတစ်ဆင့် ဘေလ်ဝယ်ပါ</h3>
              <p className="text-xs text-plum-muted leading-relaxed max-w-xs mx-auto">
                ဖုန်းဘေလ်များကို အလွယ်တကူ ဝယ်ယူပြီး မိမိဖုန်းထဲသို့ တိုက်ရိုက်လက်ခံရယူရန် PluvP Telegram Bot မှတစ်ဆင့် ဝင်ရောက်ပေးပါ။
              </p>
              <a
                href="https://t.me/pluvp_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 w-full py-2.5 px-4 bg-primary text-white rounded-xl text-xs font-semibold hover:bg-primary-hover transition shadow-sm"
              >
                <Send className="w-4 h-4" />
                <span>Telegram Bot ဖွင့်မည်</span>
              </a>
            </div>
          ) : submittedOrder ? (
            /* Order Submitted Success View */
            <div className="py-6 text-center space-y-4 animate-in zoom-in-95">
              <div className="w-14 h-14 rounded-full bg-green-500/10 text-status-green flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-base font-bold text-plum-dark">အော်ဒါ လက်ခံရရှိပါပြီ!</h3>
                <p className="text-xs text-plum-muted mt-1">
                  သင့်ထံသို့ ဖုန်းဘေလ် လွှဲပေးရန် စီစဉ်နေပါပြီ။
                </p>
              </div>

              <div className="bg-surface-page rounded-2xl border border-plum-border/80 p-4 text-left text-xs space-y-2.5">
                <div className="flex justify-between items-center py-1 border-b border-plum-border/40">
                  <span className="text-plum-muted">အော်ဒါ အမှတ်:</span>
                  <span className="font-mono font-bold text-plum-dark">{submittedOrder.id}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-plum-border/40">
                  <span className="text-plum-muted">Operator:</span>
                  <span className="font-bold text-plum-dark uppercase">{submittedOrder.operator_id}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-plum-border/40">
                  <span className="text-plum-muted">လက်ခံမည့် ဖုန်းနံပါတ်:</span>
                  <span className="font-mono font-bold text-plum-dark">{submittedOrder.recipient_phone}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-plum-border/40">
                  <span className="text-plum-muted">ရရှိမည့် ဖုန်းဘေလ်:</span>
                  <span className="font-bold text-status-green font-mono">
                    {(submittedOrder.airtime_amount || selectedDenomination?.airtime_amount || submittedOrder.amount).toLocaleString()} Ks
                  </span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-plum-muted">ကျသင့်ငွေ:</span>
                  <span className="font-bold text-primary font-mono">{submittedOrder.amount.toLocaleString()} Ks</span>
                </div>
              </div>

              <p className="text-[11px] text-plum-muted bg-primary/5 p-3 rounded-xl border border-primary/10">
                ဘေလ်လွှဲပြီးပါက Telegram မှတစ်ဆင့် သင့်ထံသို့ သတိပေးချက် အလိုအလျောက် ပေးပို့ပေးပါမည်။
              </p>

              <button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className="w-full py-2.5 px-4 bg-primary text-white rounded-xl text-xs font-bold hover:bg-primary-hover transition shadow-sm"
              >
                အော်ဒါစာရင်း ကြည့်မည်
              </button>
            </div>
          ) : (
            /* Order Input Form */
            <>
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-status-red flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* 1. Operator Selection */}
              <div>
                <label className="text-xs font-bold text-plum-dark block mb-2">
                  ၁။ Operator ရွေးချယ်ပါ
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {(['mpt', 'atom', 'mytel', 'u9'] as OperatorId[]).map((id) => {
                    const op = operators.find((o) => o.id === id);
                    const isClosed = op && op.bill_sell_status === 'closed';
                    const isSelected = operatorId === id;

                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          setOperatorId(id);
                          setError('');
                        }}
                        className={`flex flex-col items-center justify-center p-2.5 rounded-2xl border transition relative ${
                          isSelected
                            ? 'bg-primary/10 border-primary shadow-xs'
                            : 'bg-surface-page border-plum-border/70 hover:border-plum-border'
                        } ${isClosed ? 'opacity-60' : ''}`}
                      >
                        <OperatorLogo id={id} size="md" className="rounded-xl shadow-xs" />
                        <span className={`text-[11px] font-bold mt-1.5 ${isSelected ? 'text-primary' : 'text-plum-dark'}`}>
                          {id.toUpperCase()}
                        </span>
                        {isClosed && (
                          <span className="text-[9px] text-status-red font-semibold block leading-none mt-0.5">
                            ပိတ်ထားသည်
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Operator Closed Alert */}
              {isOperatorClosed && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs text-status-amber flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">{currentOperator?.name || operatorId.toUpperCase()} ဘေလ်အရောင်းကို ယာယီပိတ်ထားပါသည်</p>
                    <p className="text-[11px] opacity-80 mt-0.5">အခြား Operator ကို ရွေးချယ်နိုင်ပါသည်။</p>
                  </div>
                </div>
              )}

              {/* 2. Airtime Denomination Selection (Admin fixed amounts only) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-plum-dark block">
                    ၂။ ဝယ်ယူမည့် ဘေလ်ပမာဏ ရွေးချယ်ပါ
                  </label>
                  <span className="text-[10px] text-plum-muted font-medium">သတ်မှတ်ဈေးနှုန်းများ</span>
                </div>

                {loadingDenominations ? (
                  <div className="py-6 text-center text-xs text-plum-muted">
                    ပမာဏများ စစ်ဆေးနေပါသည်...
                  </div>
                ) : denominations.length === 0 ? (
                  <div className="py-6 text-center text-xs text-plum-muted bg-surface-page rounded-2xl border border-plum-border">
                    {operatorId.toUpperCase()} အတွက် လတ်တလော ဘေလ်ပမာဏများ မရှိသေးပါ။
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {denominations.map((den) => {
                      const isSelected = selectedDenomination?.id === den.id;
                      const savings = den.airtime_amount - den.selling_price;

                      return (
                        <button
                          key={den.id}
                          type="button"
                          onClick={() => setSelectedDenomination(den)}
                          className={`p-2.5 rounded-2xl border text-left transition relative flex flex-col justify-between ${
                            isSelected
                              ? 'bg-primary/10 border-primary shadow-xs ring-1 ring-primary'
                              : 'bg-surface-page border-plum-border/80 hover:border-primary/40'
                          }`}
                        >
                          <div>
                            <span className="font-mono font-bold text-xs text-plum-dark block">
                              {den.airtime_amount.toLocaleString()} Ks
                            </span>
                            <span className="text-[11px] font-bold text-primary block mt-0.5 font-mono">
                              {den.selling_price.toLocaleString()} Ks
                            </span>
                          </div>

                          {savings > 0 && (
                            <div className="mt-1.5 inline-flex items-center space-x-0.5 bg-green-500/10 text-status-green px-1.5 py-0.5 rounded-md text-[9px] font-semibold w-fit">
                              <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
                              <span>{savings.toLocaleString()} Ks သက်သာ</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 3. Recipient Phone Number */}
              <div>
                <label className="text-xs font-bold text-plum-dark block mb-1.5">
                  ၃။ ဘေလ်လက်ခံမည့် ဖုန်းနံပါတ်
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="09xxxxxxxxx"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3.5 py-2.5 text-xs text-plum-dark font-mono placeholder:text-plum-muted focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <span className="absolute right-3 top-2.5 text-[10px] text-plum-muted uppercase font-semibold">
                    {operatorId}
                  </span>
                </div>
              </div>

              {/* 4. Payment Method Selector */}
              <div>
                <label className="text-xs font-bold text-plum-dark block mb-2">
                  ၄။ ငွေပေးချေမှု နည်းလမ်း
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {/* Wallet Option */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('wallet')}
                    className={`p-2.5 rounded-2xl border text-center transition flex flex-col items-center justify-center ${
                      paymentMethod === 'wallet'
                        ? 'bg-primary/10 border-primary shadow-xs'
                        : 'bg-surface-page border-plum-border/70 hover:border-plum-border'
                    }`}
                  >
                    <WalletIcon className={`w-5 h-5 ${paymentMethod === 'wallet' ? 'text-primary' : 'text-plum-muted'}`} />
                    <span className="text-[11px] font-bold mt-1 text-plum-dark">PluvP Wallet</span>
                    <span className="text-[9px] text-plum-muted font-mono mt-0.5">
                      {availableBalance.toLocaleString()} Ks
                    </span>
                  </button>

                  {/* KPay Direct Option */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('direct_kpay')}
                    className={`p-2.5 rounded-2xl border text-center transition flex flex-col items-center justify-center ${
                      paymentMethod === 'direct_kpay'
                        ? 'bg-primary/10 border-primary shadow-xs'
                        : 'bg-surface-page border-plum-border/70 hover:border-plum-border'
                    }`}
                  >
                    <PaymentLogo method="kpay" size="sm" className="rounded-lg shadow-xs" />
                    <span className="text-[11px] font-bold mt-1 text-plum-dark">KBZPay</span>
                    <span className="text-[9px] text-plum-muted mt-0.5">တိုက်ရိုက်လွှဲ</span>
                  </button>

                  {/* WavePay Direct Option */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('direct_wave')}
                    className={`p-2.5 rounded-2xl border text-center transition flex flex-col items-center justify-center ${
                      paymentMethod === 'direct_wave'
                        ? 'bg-primary/10 border-primary shadow-xs'
                        : 'bg-surface-page border-plum-border/70 hover:border-plum-border'
                    }`}
                  >
                    <PaymentLogo method="wavepay" size="sm" className="rounded-lg shadow-xs" />
                    <span className="text-[11px] font-bold mt-1 text-plum-dark">WavePay</span>
                    <span className="text-[9px] text-plum-muted mt-0.5">တိုက်ရိုက်လွှဲ</span>
                  </button>
                </div>
              </div>

              {/* Direct Payment Instructions & Slip Upload */}
              {paymentMethod !== 'wallet' && (
                <div className="bg-surface-page rounded-2xl border border-plum-border p-3.5 space-y-3 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] font-bold text-plum-dark">ငွေလွှဲလက်ခံမည့် {paymentMethod === 'direct_kpay' ? 'KBZPay' : 'WavePay'} အကောင့်:</p>
                      <p className="text-xs font-mono font-bold text-primary mt-0.5">{displayNumber} ({displayName})</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopy(displayNumber)}
                      className="p-1.5 rounded-lg bg-surface-card border border-plum-border text-plum-muted hover:text-primary transition"
                      title="နံပါတ် ကူးယူရန်"
                    >
                      {copied ? <Check className="w-4 h-4 text-status-green" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-plum-dark block mb-1">
                      ငွေလွှဲပြေစာ Screenshot တင်ပေးပါ:
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {previewUrl ? (
                      <div className="relative rounded-xl overflow-hidden border border-plum-border bg-black/5 aspect-4/2 flex items-center justify-center">
                        <img src={previewUrl} alt="Slip Preview" className="h-full object-contain" />
                        <button
                          type="button"
                          onClick={() => {
                            setSlipUrl('');
                            setPreviewUrl('');
                          }}
                          className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={compressing}
                        className="w-full py-3 px-4 border border-dashed border-plum-border rounded-xl bg-surface-card hover:bg-surface-page text-xs text-plum-muted flex items-center justify-center space-x-2 transition"
                      >
                        <Upload className="w-4 h-4 text-primary" />
                        <span>{compressing ? 'ပုံရိပ် ချုံ့နေပါသည်...' : 'ပြေစာ Screenshot ရွေးချယ်ရန် နှိပ်ပါ'}</span>
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-plum-dark block mb-1">
                      Transaction ID (အပိုဆောင်း):
                    </label>
                    <input
                      type="text"
                      value={txRef}
                      onChange={(e) => setTxRef(e.target.value)}
                      placeholder="ဥပမာ: 2026092100123"
                      className="w-full bg-surface-card border border-plum-border rounded-xl px-3 py-2 text-xs font-mono text-plum-dark placeholder:text-plum-muted focus:outline-hidden focus:border-primary"
                    />
                  </div>
                </div>
              )}

              {/* Price & Summary Card */}
              {selectedDenomination && (
                <div className="bg-surface-page rounded-2xl border border-plum-border p-3.5 space-y-2 text-xs">
                  <div className="flex justify-between items-center text-plum-muted">
                    <span>ရရှိမည့် ဖုန်းဘေလ်:</span>
                    <span className="font-mono font-bold text-plum-dark">
                      {selectedDenomination.airtime_amount.toLocaleString()} Ks
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-plum-muted">
                    <span>လွှဲခ (Transfer fee):</span>
                    <span className="text-status-green font-semibold">အခမဲ့ (Included)</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-plum-border/60">
                    <span className="font-bold text-plum-dark">စုစုပေါင်း ပေးချေရန်:</span>
                    <div className="text-right">
                      <span className="font-mono font-bold text-base text-primary">
                        {selectedDenomination.selling_price.toLocaleString()} Ks
                      </span>
                      {selectedDenomination.airtime_amount > selectedDenomination.selling_price && (
                        <span className="text-[10px] text-status-green font-medium block">
                          {(selectedDenomination.airtime_amount - selectedDenomination.selling_price).toLocaleString()} Ks သက်သာမည်
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || compressing || isOperatorClosed || !selectedDenomination}
                className={`w-full py-3 px-4 rounded-xl text-xs font-bold text-white transition flex items-center justify-center space-x-2 shadow-sm ${
                  loading || compressing || isOperatorClosed || !selectedDenomination
                    ? 'bg-plum-border text-plum-muted cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-hover active:scale-[0.99]'
                }`}
              >
                {loading ? (
                  <span>အော်ဒါ တင်သွင်းနေပါသည်...</span>
                ) : (
                  <>
                    <span>ဘေလ်ဝယ်ယူရန် အတည်ပြုမည်</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
